/**
 * How a caught `wageproAuth.{signup,login}` rejection should be shown to the
 * user — the single classifier both auth screens share.
 *
 * Replaces the two byte-identical `signupErrorMessage`/`loginErrorMessage`
 * helpers that used to live in `wagepro_signup_logic.ts` and
 * `wagepro_login_logic.ts`. Both did the same thing: return `err.message`
 * verbatim for anything carrying one. That is safe for a message the SERVER
 * produced (those are deliberately generic — `docs/security-rules.md`'s
 * "Invalid credentials" / lockout carve-out copy, and
 * `wagepro_signup.ts`'s no-account-existence-oracle rules), but it also
 * leaked messages the server never produced.
 *
 * The one that reached real users: a transport failure inside the Capacitor
 * shell rendered the raw `fetch` `TypeError` — `Failed to fetch` on
 * Android/Chromium, `Load failed` on iOS/WKWebView — as red form-error copy
 * under the consent checkbox, untranslated, in a place reserved for "what
 * you typed is wrong". Nothing the user could type would fix it: the request
 * never reached `apps/api` at all (no network, an unreachable `VITE_API_URL`
 * in the shell build, a dev API that is not running, or a browser-enforced
 * CORS block — see `apps/wagepro-mobile/capacitor.config.ts`).
 *
 * Note `network` means specifically "the `fetch` itself threw". The API's own
 * CSRF rejection (`apps/api/src/middleware/csrf_origin.ts` — a real 403 whose
 * body is `{ error: 'Forbidden' }`, not a tRPC envelope) and a non-JSON 5xx
 * from a proxy (which makes tRPC's unconditional `res.json()` throw a
 * `SyntaxError`) both land in `unknown`, not `network`. That is the intended
 * split: neither is the user's connection, and neither carries a server
 * message worth showing, so both get the generic fallback.
 *
 * So the rule this module encodes is: **only a message that demonstrably came
 * back from the server may be shown verbatim.** A tRPC rejection that got a
 * response carries the deserialised error envelope on `shape`/`data`; a
 * transport failure leaves both `undefined` and wraps the original
 * `TypeError` as its `cause`. Everything else is neither, and gets the
 * generic fallback.
 *
 * Pure — no React, no I/O, no `t()`. It returns a KIND; the screens own the
 * copy, so the two non-server branches are translated like every other
 * user-visible string (CLAUDE.md § i18n).
 */

/** What to render for a caught auth-mutation rejection. */
export type AuthErrorDisplay =
  /** The request never got a response: offline, unreachable API origin, CORS/CSRF rejection. */
  | { kind: 'network' }
  /** The server answered and its message is safe to show verbatim. */
  | { kind: 'server'; message: string }
  /** Neither of the above — a thrown non-error, an empty message, a client-side bug. */
  | { kind: 'unknown' }
  /**
   * Signup-only: the server refused to create an account for this email.
   * Produced by `classifySignupError` (`wagepro_signup_logic.ts`), never by
   * `classifyAuthError` — the shared classifier cannot tell this rejection
   * apart from a wrong-password login, because the server deliberately sends
   * the same generic copy for both.
   */
  | { kind: 'signup_account_exists' };

/** Messages browsers use for a `fetch` that never completed. Chromium, WebKit, Gecko, and Node/undici respectively. */
const NETWORK_FAILURE_MESSAGE =
  /failed to fetch|load failed|networkerror|network request failed|fetch failed/i;

function readMessage(err: object): string | undefined {
  if (!('message' in err)) return undefined;
  const message = (err as { message?: unknown }).message;
  return typeof message === 'string' && message.length > 0
    ? message
    : undefined;
}

/**
 * Whether `err` carries a tRPC error SHAPE, i.e. the server answered.
 *
 * `TRPCClientError`'s constructor sets BOTH `shape = result.error` and
 * `data = result.error.data` from the same response envelope, and leaves both
 * `undefined` when the fetch itself threw. Either is therefore a valid "did
 * this reach the server" signal, and both are checked rather than just `data`:
 * `shape` is set whenever the response was a well-formed tRPC error envelope
 * AT ALL, whereas `data` additionally depends on what `apps/api`'s
 * `errorFormatter` (`src/trpc/init.ts`) chooses to attach. It always attaches
 * a `data` object today — but if that ever changed, a `data`-only check would
 * silently reclassify EVERY server rejection as `unknown`, replacing
 * "Invalid credentials" and the mandated lockout carve-out copy
 * (`docs/security-rules.md`) with a generic "something went wrong", with CI
 * still green. Checking `shape` too makes that failure mode unreachable.
 */
function hasServerErrorShape(err: object): boolean {
  const shape = 'shape' in err ? (err as { shape?: unknown }).shape : undefined;
  if (typeof shape === 'object' && shape !== null) return true;
  const data = 'data' in err ? (err as { data?: unknown }).data : undefined;
  return typeof data === 'object' && data !== null;
}

/** Whether `err` is a `fetch` transport failure rather than a server rejection. */
function isTransportFailure(err: object): boolean {
  if (err instanceof TypeError) return true;
  if ('cause' in err && (err as { cause?: unknown }).cause instanceof TypeError)
    return true;
  const message = readMessage(err);
  return message !== undefined && NETWORK_FAILURE_MESSAGE.test(message);
}

/**
 * Classify a caught `wageproAuth.{signup,login}` rejection.
 *
 * Order matters: a server rejection is checked FIRST, so a genuine server
 * message that happens to contain a network-sounding word (an upstream
 * gateway error surfaced through the API, say) is still shown as the server
 * error it is, not mislabelled as the user's connection.
 */
export function classifyAuthError(err: unknown): AuthErrorDisplay {
  // A thrown non-object (string, null, undefined) has nothing to classify —
  // every real transport failure and every tRPC rejection is an Error object.
  if (typeof err !== 'object' || err === null) return { kind: 'unknown' };
  if (hasServerErrorShape(err)) {
    const message = readMessage(err);
    return message === undefined
      ? { kind: 'unknown' }
      : { kind: 'server', message };
  }
  return isTransportFailure(err) ? { kind: 'network' } : { kind: 'unknown' };
}
